// ============================================================================
// components/mail/MailMessageView.tsx — чтение выбранного письма.
// Показывается ТОЛЬКО текст: HTML не вставляется в DOM (защита от XSS),
// при отсутствии текстовой части HTML приводится к тексту через DOMParser.
// ============================================================================

import { ArrowLeft, Mail, Paperclip } from 'lucide-react';
import type { MailAttachmentMeta, MailMessage, MailMessageDetail } from '@/lib/mail/mailData';
import { plainText, senderLabel } from '@/lib/mail/mailData';
import { formatValue } from '@/lib/format';
import { Button } from '@/components/ui/button';

interface MailMessageViewProps {
  message: MailMessage | null;
  detail: MailMessageDetail | null;
  attachments: MailAttachmentMeta[];
  loading: boolean;
  onBack?: () => void;
}

function formatSize(size: number): string {
  if (!size || size < 0) return '';
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} КБ`;
  return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground">
      <Mail className="h-10 w-10 opacity-40" />
      <div className="text-sm font-medium">Выберите письмо</div>
      <div className="max-w-xs text-xs">
        Слева — письма выбранной папки. Здесь появятся отправитель, тема и текст.
      </div>
    </div>
  );
}

export function MailMessageView({
  message,
  detail,
  attachments,
  loading,
  onBack,
}: MailMessageViewProps) {
  if (!message) return <EmptyState />;

  const body = detail
    ? (detail.textBody || plainText(detail.htmlBody) || '').trim()
    : '';
  const date = formatValue(message.receivedAt || message.createdAt, '', 'timestamp');
  const from = senderLabel(message);
  const fromAddress = message.fromAddress && message.fromAddress !== from ? message.fromAddress : '';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b px-3 py-2">
        <div className="flex items-start gap-2">
          {onBack && (
            <Button size="sm" variant="ghost" className="mt-0.5 -ml-1 h-7 px-1.5" onClick={onBack} title="К списку">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold leading-snug">{message.subject || '(без темы)'}</h2>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
              <span className="text-sm text-foreground">{from}</span>
              {fromAddress && <span>&lt;{fromAddress}&gt;</span>}
              <span>·</span>
              <span>{date}</span>
            </div>
          </div>
        </div>
        {attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {attachments.map((attachment, index) => (
              <span
                key={`${attachment.filename}-${index}`}
                className="inline-flex max-w-full items-center gap-1 rounded border bg-muted/40 px-2 py-0.5 text-xs"
                title={attachment.contentType}
              >
                <Paperclip className="h-3 w-3 shrink-0" />
                <span className="truncate">{attachment.filename}</span>
                {attachment.size > 0 && (
                  <span className="shrink-0 text-muted-foreground">{formatSize(attachment.size)}</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {loading && <div className="text-sm text-muted-foreground">Загрузка письма…</div>}
        {!loading && !body && (
          <div className="text-sm text-muted-foreground">Нет текстового содержимого письма.</div>
        )}
        {!loading && body && (
          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{body}</div>
        )}
      </div>
    </div>
  );
}
